import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useAnonymousSale } from 'src/common/contexts/anonymous-sale-context';
import { type CampaignConfig, CampaignElementType } from 'src/common/interfaces/campaigns';
import { useGetUserQuery } from 'src/common/slices';
import { InviteTopBar } from 'src/features/invite/invite-banner';
import { anonymousSaleConfig, CampaignTypes } from '../../campaigns.config';
import { checkIfPathMatches } from '../../helpers';
import { useActiveCampaign } from '../../hooks/use-active-campaign';
import { useNotApplicableCampaign } from '../../hooks/use-not-applicable-campaign';
import type { CampaignElementProps } from './campaign-element.types';
import { NotApplicableCampaign } from './not-applicable-campaign/not-applicable-campaign';
export const CampaignElement = ({
  type
}: CampaignElementProps) => {
  const {
    campaign,
    shouldPromote
  } = useActiveCampaign();
  const {
    pathname
  } = useLocation();
  const {
    data: userDataResponse
  } = useGetUserQuery();
  const {
    notApplicableCampaign
  } = useNotApplicableCampaign();
  const anonymousSale = useAnonymousSale();
  const userData = userDataResponse?.data;
  const isEmbeddedBar = type === CampaignElementType.EMBEDDED_BAR;
  const hasCampaignFriendlyName = notApplicableCampaign?.campaignFriendlyName !== '';
  const isWhySubscribePage = pathname === '/why-subscribe';
  const isSubscriptionPage = pathname === '/manage-account/subscription';
  const campaignConfig = useMemo<CampaignConfig | null>(() => {
    if (!campaign || !campaign.type || !(campaign.type in CampaignTypes)) return null;
    const campaignConfigs = CampaignTypes[campaign.type](campaign.name, campaign.isFlashSale, campaign.applicableIf);
    const config = campaignConfigs?.[type];
    if (!config) {
      console.warn(`${type} is not configured for this campaign: ${campaign.name}`);
      return null;
    }
    const {
      pathsToExclude,
      pathsToInclude,
      shouldShow
    } = config;
    const showConditionResolves = !shouldShow || shouldShow(pathname, userData);
    const pathMatches = checkIfPathMatches(pathsToExclude || [], pathsToInclude || [], pathname);
    if (!showConditionResolves || !pathMatches) return null;
    return config;
  }, [campaign, pathname, userData, type]);
  if (isEmbeddedBar && hasCampaignFriendlyName && (isWhySubscribePage || isSubscriptionPage)) {
    return <NotApplicableCampaign influencerName={notApplicableCampaign?.campaignFriendlyName} />;
  }
  if (campaignConfig && shouldPromote) {
    return campaignConfig.renderComponent(userData?.user.isPremium);
  }

  // Frontend only campaign
  if (anonymousSale.isActive) {
    const config = anonymousSaleConfig[type];
    if (config) {
      const {
        pathsToExclude,
        pathsToInclude,
        shouldShow
      } = config;
      const showConditionResolves = !shouldShow || shouldShow(pathname, userData);
      const pathMatches = checkIfPathMatches(pathsToExclude || [], pathsToInclude || [], pathname);
      if (showConditionResolves && pathMatches) {
        return config.renderComponent();
      }
    }
  }
  if (type === CampaignElementType.TOP_BAR) {
    // InviteBanner only shows if no Campaign top bar is active
    return <InviteTopBar />;
  }
  return null;
};